import type { LedgerPayment, ListParams } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

/** Every method the server records (Payment.PAYMENT_METHODS), in the same order. */
export const PAYMENT_METHODS = [
  ["cash", "Cash"],
  ["bank_transfer", "Bank transfer"],
  ["mobile_money", "Mobile money"],
  ["cheque", "Cheque"],
  ["surepay", "SurePay"],
  ["pesapal", "PesaPal"],
  ["mtn_mobile_money", "MTN Mobile Money"],
  ["airtel_money", "Airtel Money"],
  ["africell_money", "Africell Money"],
  ["card", "Card payment"],
  ["other", "Other"],
] as const;

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  part_refunded: "Partly refunded",
  failed: "Failed",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

export const PAYMENT_STATUS_DOTS: Record<string, string> = {
  pending: "bg-amber-500",
  processing: "bg-amber-500",
  completed: "bg-emerald-500",
  part_refunded: "bg-sky-500",
  refunded: "bg-gray-400",
  failed: "bg-red-500",
  cancelled: "bg-gray-400",
};

/** Server ordering fields (PaymentListView.ordering_fields). */
export const PAYMENT_ORDERINGS = {
  "-payment_date": "Newest first",
  payment_date: "Oldest first",
  "-amount": "Amount (highest first)",
  amount: "Amount (lowest first)",
  receipt_number: "Receipt number",
} as const;

export const PAYMENT_PAGE_SIZES = [10, 25, 50, 100];

export interface PaymentRegisterFilters {
  search: string;
  method: string;
  status: string;
  fee: string;
  /** "credit": only receipts still holding money that isn't against an invoice. */
  allocation: "" | "credit";
  dateFrom: string;
  dateTo: string;
  ordering: keyof typeof PAYMENT_ORDERINGS;
}

export const EMPTY_PAYMENT_FILTERS: PaymentRegisterFilters = {
  search: "",
  method: "",
  status: "",
  fee: "",
  allocation: "",
  dateFrom: "",
  dateTo: "",
  ordering: "-payment_date",
};

export const paymentMethodLabel = (method: string) =>
  PAYMENT_METHODS.find(([value]) => value === method)?.[1] ?? method.replace(/_/g, " ");

export const paymentStatusLabel = (status: string) => PAYMENT_STATUS_LABELS[status] ?? status.replace(/_/g, " ");

/** What the server is asked for; the search is passed in so the caller can defer it while typing. */
export function paymentRegisterParams(filters: PaymentRegisterFilters, search = filters.search): ListParams {
  return {
    search: search.trim(),
    payment_method: filters.method,
    payment_status: filters.status,
    fee: filters.fee,
    unallocated: filters.allocation === "credit" ? "true" : undefined,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    ordering: filters.ordering,
  };
}

/** Filters that narrow the register; the sort order doesn't count. */
export const countPaymentFilters = (filters: PaymentRegisterFilters) =>
  [
    filters.search.trim(),
    filters.method,
    filters.status,
    filters.fee,
    filters.allocation,
    filters.dateFrom || filters.dateTo,
  ].filter(Boolean).length;

const isoDay = (day: Date) =>
  `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;

export const PAYMENT_PERIODS = {
  all: "All time",
  today: "Today",
  week: "This week",
  month: "This month",
} as const;
export type PaymentPeriod = keyof typeof PAYMENT_PERIODS;

/** The date range a quick period stands for; weeks start on Monday. */
export function periodRange(period: PaymentPeriod, today = new Date()): Pick<PaymentRegisterFilters, "dateFrom" | "dateTo"> {
  if (period === "all") return { dateFrom: "", dateTo: "" };
  const end = isoDay(today);
  if (period === "today") return { dateFrom: end, dateTo: end };
  const start = new Date(today);
  if (period === "week") start.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  else start.setDate(1);
  return { dateFrom: isoDay(start), dateTo: end };
}

/** Which quick period the dates match, or null for a custom range. */
export function activePeriod(filters: PaymentRegisterFilters, today = new Date()): PaymentPeriod | null {
  return (
    (Object.keys(PAYMENT_PERIODS) as PaymentPeriod[]).find((period) => {
      const range = periodRange(period, today);
      return range.dateFrom === filters.dateFrom && range.dateTo === filters.dateTo;
    }) ?? null
  );
}

export const paymentInvoices = (payment: LedgerPayment) =>
  payment.allocations?.length
    ? payment.allocations.map((allocation) => allocation.invoice_number).join(", ")
    : payment.invoice_number || "";

export const paymentDay = (payment: LedgerPayment) => payment.payment_date?.slice(0, 10) ?? "";

export function paymentExportColumns(): ExportColumn<LedgerPayment>[] {
  return [
    { header: "Date", accessor: paymentDay },
    { header: "Receipt no.", accessor: (payment) => payment.receipt_number || "" },
    { header: "Student", accessor: (payment) => payment.student_name },
    { header: "Fee", accessor: (payment) => payment.fee_name || "PRN receipt" },
    { header: "Method", accessor: (payment) => paymentMethodLabel(payment.payment_method) },
    { header: "Reference", accessor: (payment) => payment.reference_number || "" },
    { header: "Received into", accessor: (payment) => payment.receipt_account_name || "" },
    { header: "Invoices", accessor: (payment) => paymentInvoices(payment) || "Held as credit" },
    { header: "Amount (UGX)", accessor: (payment) => Number(payment.amount) },
    { header: "Allocated (UGX)", accessor: (payment) => Number(payment.allocated_amount || 0) },
    { header: "Credit held (UGX)", accessor: (payment) => Number(payment.unallocated_amount || 0) },
    { header: "Refunded (UGX)", accessor: (payment) => Number(payment.refunded_amount || 0) },
    { header: "Status", accessor: (payment) => paymentStatusLabel(payment.payment_status) },
    { header: "Recorded by", accessor: (payment) => payment.recorded_by_name || "" },
  ];
}
